import { useState } from "react";
import { Plus, Trash2, Sparkles, X } from "lucide-react";
import { fmtCurrency, fmtPSF, fmtPercent } from "../utils/format";

interface RentStep {
  year: number;
  rent_annual_psf: number;
}

interface RentStepEditorProps {
  tenantIndex: number;
  tenantName: string;
  leasedSf: number;
  leaseStart: string;
  leaseEnd: string;
  currentSteps: RentStep[];
  escalationType: string;
  escalationValue: number;
  basePsf: number;
  onSave: (steps: RentStep[], escalationType: string) => Promise<void>;
  onClose: () => void;
}

export default function RentStepEditor({
  tenantName,
  leasedSf,
  leaseStart,
  leaseEnd,
  currentSteps,
  escalationType,
  escalationValue,
  basePsf,
  onSave,
  onClose,
}: RentStepEditorProps) {
  const [steps, setSteps] = useState<RentStep[]>(() =>
    currentSteps.length > 0
      ? [...currentSteps].sort((a, b) => a.year - b.year)
      : []
  );
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const startYear = leaseStart ? new Date(leaseStart).getFullYear() : new Date().getFullYear();
  const endYear = leaseEnd ? new Date(leaseEnd).getFullYear() : startYear + 10;

  function fillFromEscalation() {
    const filled: RentStep[] = [];
    let psf = basePsf;
    for (let yr = startYear; yr <= endYear; yr++) {
      if (yr > startYear) {
        if (escalationType === "fixed_percent" || escalationType === "CPI") {
          psf = psf * (1 + (escalationValue || 0));
        } else if (escalationType === "fixed_amount") {
          psf = psf + (escalationValue || 0);
        }
      }
      filled.push({ year: yr, rent_annual_psf: Math.round(psf * 100) / 100 });
    }
    setSteps(filled);
    setEditing(true);
  }

  function addYear() {
    const lastYear = steps.length > 0 ? Math.max(...steps.map(s => s.year)) : startYear - 1;
    const lastPsf = steps.length > 0 ? steps[steps.length - 1].rent_annual_psf : basePsf;
    setSteps([...steps, { year: lastYear + 1, rent_annual_psf: lastPsf }]);
  }

  function removeStep(idx: number) {
    setSteps(steps.filter((_, i) => i !== idx));
  }

  function updateStep(idx: number, field: keyof RentStep, value: string) {
    const updated = [...steps];
    if (field === "year") {
      updated[idx] = { ...updated[idx], year: parseInt(value) || 0 };
    } else {
      updated[idx] = { ...updated[idx], rent_annual_psf: parseFloat(value) || 0 };
    }
    setSteps(updated);
  }

  async function handleSave() {
    setSaving(true);
    try {
      const sorted = [...steps].sort((a, b) => a.year - b.year);
      const escType = sorted.length > 0 ? "flat" : escalationType;
      await onSave(sorted, escType);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  function handleClear() {
    setSteps([]);
    setEditing(true);
  }

  return (
    <div className="bg-white rounded-xl border border-grove-300 shadow-sm p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-bark-700">
          Rent Schedule — {tenantName}
        </h4>
        <button onClick={onClose} className="text-bark-300 hover:text-bark-500">
          <X className="w-4 h-4" />
        </button>
      </div>

      <p className="text-[11px] text-bark-400">
        {steps.length > 0
          ? `Stepped: ${steps.length} steps defined`
          : `Escalation: ${escalationType === "fixed_percent" ? `${fmtPercent(escalationValue, 1)} annual` : escalationType === "fixed_amount" ? `${fmtPSF(escalationValue)}/yr` : escalationType === "CPI" ? `CPI + ${fmtPercent(escalationValue, 1)}` : "Flat"}`}
      </p>

      {steps.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="border-b border-grove-200">
                <th className="text-right py-1 px-2 text-[10px] font-semibold uppercase text-bark-400">Year</th>
                <th className="text-right py-1 px-2 text-[10px] font-semibold uppercase text-bark-400">Rent PSF</th>
                <th className="text-right py-1 px-2 text-[10px] font-semibold uppercase text-bark-400">Annual Rent</th>
                <th className="text-right py-1 px-2 text-[10px] font-semibold uppercase text-bark-400">Change</th>
                {editing && <th className="w-6"></th>}
              </tr>
            </thead>
            <tbody>
              {steps.map((step, idx) => {
                const prevPsf = idx > 0 ? steps[idx - 1].rent_annual_psf : null;
                const change = prevPsf && prevPsf > 0
                  ? fmtPercent((step.rent_annual_psf - prevPsf) / prevPsf, 1)
                  : "—";
                return (
                  <tr key={idx} className="border-b border-grove-100">
                    <td className="py-1 px-2 text-right tabular-nums whitespace-nowrap">
                      {editing ? (
                        <input type="number" value={step.year}
                          onChange={(e) => updateStep(idx, "year", e.target.value)}
                          className="w-16 px-1 py-0.5 text-[12px] border border-grove-200 rounded tabular-nums" />
                      ) : step.year}
                    </td>
                    <td className="py-1 px-2 text-right tabular-nums">
                      {editing ? (
                        <input type="number" step="0.01" value={step.rent_annual_psf}
                          onChange={(e) => updateStep(idx, "rent_annual_psf", e.target.value)}
                          className="w-20 px-1 py-0.5 text-[12px] text-right border border-grove-200 rounded tabular-nums" />
                      ) : fmtPSF(step.rent_annual_psf)}
                    </td>
                    <td className="py-1 px-2 text-right tabular-nums text-bark-400">
                      {fmtCurrency(step.rent_annual_psf * leasedSf)}
                    </td>
                    <td className="py-1 px-2 text-right tabular-nums text-bark-400">{change}</td>
                    {editing && (
                      <td className="py-1 px-1">
                        <button onClick={() => removeStep(idx)} className="text-bark-300 hover:text-red-500">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        {!editing ? (
          <>
            <button onClick={() => setEditing(true)}
              className="px-3 py-1.5 text-[11px] font-semibold text-forest-700 border border-forest-300 rounded-lg hover:bg-forest-50 transition-colors">
              Edit Steps
            </button>
            <button onClick={fillFromEscalation}
              className="px-3 py-1.5 text-[11px] font-semibold text-honey-700 border border-honey-300 rounded-lg hover:bg-honey-50 transition-colors flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> Fill from Escalation
            </button>
            {steps.length > 0 && (
              <button onClick={handleClear}
                className="px-3 py-1.5 text-[11px] font-semibold text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors">
                Clear All
              </button>
            )}
          </>
        ) : (
          <>
            <button onClick={addYear}
              className="px-3 py-1.5 text-[11px] font-semibold text-forest-700 border border-forest-300 rounded-lg hover:bg-forest-50 transition-colors flex items-center gap-1">
              <Plus className="w-3 h-3" /> Add Year
            </button>
            <button onClick={handleSave} disabled={saving}
              className="px-3 py-1.5 text-[11px] font-semibold text-white bg-forest-600 rounded-lg hover:bg-forest-500 disabled:opacity-50 transition-colors">
              {saving ? "Saving..." : "Save"}
            </button>
            <button onClick={() => { setSteps(currentSteps); setEditing(false); }}
              className="px-3 py-1.5 text-[11px] font-semibold text-bark-500 border border-grove-300 rounded-lg hover:bg-grove-50 transition-colors">
              Cancel
            </button>
          </>
        )}
      </div>
    </div>
  );
}
